import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const InvoiceCreator = () => {
  const [formData, setFormData] = useState({
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    products: [{ name: '', details: '', hsnCode: '8517', customHsnCode: '', quantity: 1, unitPrice: 0, total: 0 }],
    discount: 0
  });

  const [calculations, setCalculations] = useState({
    subtotal: 0,
    gstAmount: 0,
    totalAmount: 0
  });

  const [roundTotal, setRoundTotal] = useState(0);
  const [calculationMode, setCalculationMode] = useState('normal'); // 'normal' or 'reverse'

  useEffect(() => {
    if (calculationMode === 'normal') {
      calculateTotals();
    } else {
      calculateReverseTotals();
    }
  }, [formData.products, formData.discount, roundTotal, calculationMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.products];
    updatedProducts[index] = {
      ...updatedProducts[index],
      [field]: value
    };

    // Calculate total for this product
    if (field === 'quantity' || field === 'unitPrice') {
      const quantity = field === 'quantity' ? parseFloat(value) : parseFloat(updatedProducts[index].quantity);
      const unitPrice = field === 'unitPrice' ? parseFloat(value) : parseFloat(updatedProducts[index].unitPrice);
      updatedProducts[index].total = quantity * unitPrice;
    }

    setFormData(prev => ({
      ...prev,
      products: updatedProducts
    }));
  };

  const addProduct = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { name: '', details: '', hsnCode: '8517', customHsnCode: '', quantity: 1, unitPrice: 0, total: 0 }]
    }));
  };

  const removeProduct = (index) => {
    if (formData.products.length > 1) {
      const updatedProducts = formData.products.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        products: updatedProducts
      }));
    } else {
      toast.warning('At least one product is required');
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.products.reduce((sum, product) => sum + (product.total || 0), 0);
    const afterDiscount = subtotal - (parseFloat(formData.discount) || 0);
    const gstAmount = afterDiscount * 0.18;
    const totalAmount = afterDiscount + gstAmount;

    setCalculations({
      subtotal,
      gstAmount,
      totalAmount
    });
  };

  const calculateReverseTotals = () => {
    if (roundTotal <= 0) return;

    const discount = parseFloat(formData.discount) || 0;
    
    // Reverse calculation: Total = (Subtotal - Discount) + GST
    // Total = (Subtotal - Discount) * 1.18
    // Subtotal - Discount = Total / 1.18
    const subtotalAfterDiscount = roundTotal / 1.18;
    const subtotal = subtotalAfterDiscount + discount;
    const gstAmount = subtotalAfterDiscount * 0.18;

    setCalculations({
      subtotal,
      gstAmount,
      totalAmount: roundTotal
    });

    // Distribute the subtotal among products proportionally
    const totalProductValue = formData.products.reduce((sum, product) => sum + (product.quantity * product.unitPrice), 0);
    
    if (totalProductValue > 0) {
      const updatedProducts = formData.products.map(product => {
        const productValue = product.quantity * product.unitPrice;
        const ratio = productValue / totalProductValue;
        const newTotal = subtotal * ratio;
        const newUnitPrice = newTotal / product.quantity;
        
        return {
          ...product,
          unitPrice: newUnitPrice,
          total: newTotal
        };
      });
      
      setFormData(prev => ({
        ...prev,
        products: updatedProducts
      }));
    }
  };

  const generateInvoiceNumber = () => {
    return 'INV-' + Date.now();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const saveInvoice = () => {
    if (!formData.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (formData.products.some(p => !p.name.trim() || !p.quantity || !p.unitPrice)) {
      toast.error('Please fill all product details');
      return;
    }

    // Create invoice data
    const invoiceData = {
      ...formData,
      invoiceNumber: generateInvoiceNumber(),
      calculations,
      timestamp: Date.now()
    };

    // Save to localStorage
    const savedInvoices = JSON.parse(localStorage.getItem('a2ztak_invoices') || '[]');
    savedInvoices.push(invoiceData);
    localStorage.setItem('a2ztak_invoices', JSON.stringify(savedInvoices));

    toast.success('Invoice saved successfully! You can upload it later when adding sales.');
    
    // Reset form
    setFormData({
      customerName: '',
      customerPhone: '',
      customerAddress: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      products: [{ name: '', details: '', hsnCode: '8517', customHsnCode: '', quantity: 1, unitPrice: 0, total: 0 }],
      discount: 0
    });
  };

  const downloadPDF = () => {
    if (!formData.customerName.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (formData.products.some(p => !p.name.trim() || !p.quantity || !p.unitPrice)) {
      toast.error('Please fill all product details');
      return;
    }

    // Create PDF using jsPDF
    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();
    
    // Set font
    doc.setFont('helvetica');
    
    // Colors - Professional Green Theme like Zoho
    const primaryColor = [34, 139, 34]; // Forest Green
    const darkColor = [0, 0, 0]; // Black
    const lightGray = [245, 245, 245]; // Light gray
    const accentColor = [50, 205, 50]; // Lime Green
    
    // Create Logo (Simple geometric design)
    doc.setFillColor(...primaryColor);
    doc.circle(25, 20, 8, 'F'); // Main circle
    doc.setFillColor(...accentColor);
    doc.circle(25, 20, 5, 'F'); // Inner circle
    
    // Company Name and Details (Right side of logo)
    doc.setTextColor(...darkColor);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('A2Z TAK', 40, 18);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Electronics & Technology Solutions', 40, 24);
    doc.text('Punjab, India', 40, 30);
    doc.text('Phone: +91-XXXXX-XXXXX', 40, 36);
    doc.text('GST No: 03ABCDE1234F1Z5', 40, 42);
    doc.text('Email: info@a2ztak.in', 40, 48);
    
    // Invoice Title and Number (Right side)
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE', 150, 18);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const invoiceNumber = generateInvoiceNumber();
    doc.text(`Invoice# ${invoiceNumber}`, 150, 30);
    
    // Bill To Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Bill To:', 20, 65);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(formData.customerName, 20, 75);
    if (formData.customerPhone) {
      doc.text(formData.customerPhone, 20, 80);
    }
    if (formData.customerAddress) {
      const addressLines = doc.splitTextToSize(formData.customerAddress, 60);
      doc.text(addressLines, 20, 85);
    }
    
    // Invoice Details Bar (Green background)
    doc.setFillColor(...primaryColor);
    doc.rect(20, 100, 170, 8, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Invoice Date', 25, 106);
    doc.text('Terms', 100, 106);
    doc.text('Due Date', 150, 106);
    
    // Invoice Details Values
    doc.setTextColor(...darkColor);
    doc.setFont('helvetica', 'normal');
    doc.text(formData.invoiceDate, 25, 115);
    doc.text('Cash/UPI', 100, 115);
    doc.text(formData.invoiceDate, 150, 115);
    
    // Products Table Header (Green background)
    doc.setFillColor(...primaryColor);
    doc.rect(20, 125, 170, 8, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('#', 25, 131);
    doc.text('Item & Description', 35, 131);
    doc.text('HSN', 100, 131);
    doc.text('Qty', 120, 131);
    doc.text('Rate', 140, 131);
    doc.text('Tax%', 160, 131);
    doc.text('Amount', 175, 131);
    
    // Products Table Rows
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    let yPosition = 140;
    
    formData.products.forEach((product, index) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Product row
      doc.text((index + 1).toString(), 25, yPosition);
      doc.text(product.name.substring(0, 25), 35, yPosition);
      const hsnCode = product.hsnCode === 'custom' ? product.customHsnCode : product.hsnCode;
      doc.text((hsnCode || '8517').substring(0, 6), 100, yPosition);
      doc.text(product.quantity.toString(), 120, yPosition);
      doc.text(`₹${formatAmount(product.unitPrice)}`, 140, yPosition);
      doc.text('18%', 160, yPosition);
      doc.text(`₹${formatAmount(product.total)}`, 175, yPosition);
      
      // Product description (IMEI/Details) in smaller font
      if (product.details) {
        doc.setFontSize(8);
        doc.text(product.details.substring(0, 30), 35, yPosition + 4);
        doc.setFontSize(9);
      }
      
      yPosition += 12;
    });
    
    // Summary Section (Right aligned)
    yPosition = Math.max(yPosition + 10, 200);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Sub Total:', 120, yPosition);
    doc.text(`₹${formatAmount(calculations.subtotal)}`, 180, yPosition, { align: 'right' });
    yPosition += 8;
    
    if (parseFloat(formData.discount) > 0) {
      doc.text(`Discount (${((parseFloat(formData.discount) / calculations.subtotal) * 100).toFixed(1)}%):`, 120, yPosition);
      doc.text(`(-)₹${formatAmount(parseFloat(formData.discount))}`, 180, yPosition, { align: 'right' });
      yPosition += 8;
    }
    
    doc.text('GST (18%):', 120, yPosition);
    doc.text(`₹${formatAmount(calculations.gstAmount)}`, 180, yPosition, { align: 'right' });
    yPosition += 8;
    
    // Total Amount (Bold)
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Total:', 120, yPosition);
    doc.text(`₹${formatAmount(calculations.totalAmount)}`, 180, yPosition, { align: 'right' });
    yPosition += 8;
    
    doc.text('Balance Due:', 120, yPosition);
    doc.text(`₹${formatAmount(calculations.totalAmount)}`, 180, yPosition, { align: 'right' });
    
    // Footer
    yPosition += 20;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Thanks for your business.', 20, yPosition);
    doc.text('Terms & Conditions:', 20, yPosition + 10);
    doc.text('All electronic products are covered under manufacturer warranty.', 20, yPosition + 16);
    doc.text('GST applicable as per government norms.', 20, yPosition + 22);
    
    // Save the PDF
    const fileName = `A2Z_TAK_Invoice_${formData.customerName.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
    doc.save(fileName);
    
    toast.success('Professional PDF invoice downloaded successfully!');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invoice Creator</h1>
            <p className="text-gray-600">Create and save invoices locally</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={downloadPDF}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Download PDF
            </button>
            <button
              onClick={saveInvoice}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Save & Upload Later
            </button>
          </div>
        </div>

        <form className="space-y-6">
          {/* Customer Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Name *
              </label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Phone
              </label>
              <input
                type="tel"
                name="customerPhone"
                value={formData.customerPhone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer Address
              </label>
              <textarea
                name="customerAddress"
                value={formData.customerAddress}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Date
              </label>
              <input
                type="date"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Products Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Products</h3>
              <button
                type="button"
                onClick={addProduct}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
              >
                + Add Product
              </button>
            </div>

            <div className="space-y-4">
              {formData.products.map((product, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) => handleProductChange(index, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        IMEI/Details
                      </label>
                      <input
                        type="text"
                        value={product.details}
                        onChange={(e) => handleProductChange(index, 'details', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        HSN Code
                      </label>
                      <select
                        value={product.hsnCode}
                        onChange={(e) => handleProductChange(index, 'hsnCode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="8517">8517 - Mobile Phones (95%)</option>
                        <option value="8528">8528 - TV & Monitors</option>
                        <option value="8418">8418 - Refrigerators</option>
                        <option value="8450">8450 - Washing Machines</option>
                        <option value="8414">8414 - Air Conditioners</option>
                        <option value="8516">8516 - Electric Appliances</option>
                        <option value="8471">8471 - Laptops & Tablets</option>
                        <option value="8518">8518 - Headphones & Speakers</option>
                        <option value="8504">8504 - Chargers & Adapters</option>
                        <option value="custom">Custom HSN Code</option>
                      </select>
                      {product.hsnCode === 'custom' && (
                        <input
                          type="text"
                          placeholder="Enter custom HSN code"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2"
                          onChange={(e) => handleProductChange(index, 'customHsnCode', e.target.value)}
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity *
                      </label>
                      <input
                        type="number"
                        value={product.quantity}
                        onChange={(e) => handleProductChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Price *
                      </label>
                      <input
                        type="number"
                        value={product.unitPrice}
                        onChange={(e) => handleProductChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total
                        </label>
                        <input
                          type="text"
                          value={formatAmount(product.total)}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        />
                      </div>
                      {formData.products.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProduct(index)}
                          className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Calculation Mode Selection */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Calculation Mode</h3>
            <div className="flex space-x-4 mb-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="calculationMode"
                  value="normal"
                  checked={calculationMode === 'normal'}
                  onChange={(e) => setCalculationMode(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Normal Calculation</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="calculationMode"
                  value="reverse"
                  checked={calculationMode === 'reverse'}
                  onChange={(e) => setCalculationMode(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">Reverse Calculation (Round Total)</span>
              </label>
            </div>
            
            {calculationMode === 'reverse' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Total Amount Received from Customer (Round Figure) *
                </label>
                <input
                  type="number"
                  value={roundTotal}
                  onChange={(e) => setRoundTotal(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter total amount"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the exact amount customer paid. GST and item prices will be calculated automatically.
                </p>
              </div>
            )}
          </div>

          {/* Calculation Section */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Calculation</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Subtotal:</span>
                <span className="font-semibold text-gray-900">₹{formatAmount(calculations.subtotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Discount:</span>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                    placeholder="0"
                    disabled={false}
                  />
                  <span className="text-sm text-gray-500">₹</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">GST (18%):</span>
                <span className="font-semibold text-gray-900">₹{formatAmount(calculations.gstAmount)}</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-300">
                <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                <span className="text-lg font-bold text-blue-600">₹{formatAmount(calculations.totalAmount)}</span>
              </div>
              
              {calculationMode === 'reverse' && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-700">
                    <strong>Reverse Calculation Active:</strong> Item prices and GST have been calculated based on the total amount received from customer.
                  </p>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceCreator;
